<template>
    <Toast />
    <div class="grid">
        <div class="col-12">
            <div class="card">
                <div class="flex flex-column md:flex-row md:justify-content-between md:align-items-center mb-3">
                    <div>
                        <h5 class="m-0">Ajustes de Inventario</h5>
                        <small class="text-color-secondary">Cada vez que el stock se corrigió a mano: quién, cuándo, cuánto y cuánto valía.</small>
                    </div>
                    <div class="flex gap-2 mt-2 md:mt-0">
                        <Button label="Conteos" icon="pi pi-list" class="p-button-text" @click="$router.push('/dashboard/inventario/conteo/historial')" />
                        <Button label="Lista de compras" icon="pi pi-shopping-cart" class="p-button-text" @click="$router.push('/dashboard/inventario/lista-compras')" />
                        <Button label="Sincronizar stock" icon="pi pi-sync" class="p-button-outlined" @click="$router.push('/dashboard/inventario/sincronizar')" />
                    </div>
                </div>

                <div class="flex flex-column md:flex-row md:flex-wrap md:justify-content-end md:align-items-end gap-2 mb-3">
                    <div>
                        <label class="block text-sm">Producto</label>
                        <AutoComplete v-model="filters.product" :suggestions="productSuggestions" field="name" :dropdown="true" forceSelection
                                      placeholder="Todos" @complete="searchProducts($event.query)" />
                    </div>
                    <div>
                        <label class="block text-sm">Origen</label>
                        <Dropdown v-model="filters.source" :options="sourceOptions" optionLabel="label" optionValue="value" placeholder="Todos" showClear />
                    </div>
                    <div>
                        <label class="block text-sm">Motivo</label>
                        <Dropdown v-model="filters.reasonCode" :options="reasonOptions" optionLabel="label" optionValue="value" placeholder="Todos" showClear />
                    </div>
                    <div>
                        <label class="block text-sm">Desde</label>
                        <Calendar v-model="filters.initialDate" dateFormat="yy-mm-dd" showIcon />
                    </div>
                    <div>
                        <label class="block text-sm">Hasta</label>
                        <Calendar v-model="filters.endDate" dateFormat="yy-mm-dd" showIcon />
                    </div>
                    <div class="flex align-items-center gap-2 pb-2">
                        <Checkbox id="review" v-model="filters.needsReview" :binary="true" />
                        <label for="review" class="text-sm">Solo para revisar</label>
                    </div>
                    <div>
                        <Button icon="pi pi-search" class="p-button-rounded apply-filter-btn" v-tooltip.top="'Aplicar filtros'" @click="loadAll(1)" />
                    </div>
                </div>

                <div class="grid text-center mb-3">
                    <div class="col-12 md:col"><div class="surface-100 border-round p-3"><div class="text-sm text-color-secondary">Pérdidas</div><div class="font-bold text-xl text-red-500">{{ formatMoney(totals.lossValue) }}</div></div></div>
                    <div class="col-12 md:col"><div class="surface-100 border-round p-3"><div class="text-sm text-color-secondary">Ganancias</div><div class="font-bold text-xl text-blue-500">{{ formatMoney(totals.gainValue) }}</div></div></div>
                    <div class="col-12 md:col"><div class="surface-100 border-round p-3"><div class="text-sm text-color-secondary">Neto</div><div class="font-bold text-xl" :class="diffClass(totals.netValue)">{{ formatMoney(totals.netValue) }}</div></div></div>
                    <!-- El consumo normal de los insumos periódicos no es una pérdida: va aparte. -->
                    <div v-if="hasConsumption" class="col-12 md:col"><div class="surface-100 border-round p-3"><div class="text-sm text-color-secondary">Consumo de insumos</div><div class="font-bold text-xl">{{ formatMoney(totals.consumptionValue) }}</div></div></div>
                </div>

                <TabView>
                    <TabPanel header="Bitácora">
                        <DataTable
                            :value="items"
                            :loading="loading"
                            :paginator="true"
                            :rows="pageSize"
                            :totalRecords="totalItems"
                            :lazy="true"
                            :first="(currentPage - 1) * pageSize"
                            @page="loadList($event.page + 1)"
                            responsiveLayout="scroll"
                            class="p-datatable-sm"
                        >
                            <template #empty>No hay ajustes en este período.</template>
                            <Column header="Fecha"><template #body="{ data }">{{ formatDate(data.createdAt) }}</template></Column>
                            <Column field="productName" header="Producto" />
                            <Column header="Origen">
                                <template #body="{ data }">
                                    <a v-if="data.inventoryCountId" href="javascript:void(0)" @click="openCount(data.inventoryCountId)">
                                        {{ sourceLabel(data.source) }} #{{ data.inventoryCountId }}
                                    </a>
                                    <span v-else>{{ sourceLabel(data.source) }}</span>
                                </template>
                            </Column>
                            <Column header="Usuario">
                                <template #body="{ data }">{{ data.creator }} <Tag :value="data.creatorRol" severity="info" class="ml-1" /></template>
                            </Column>
                            <Column header="Anterior"><template #body="{ data }">{{ formatQty(data.previousStock) }}</template></Column>
                            <Column header="Nuevo"><template #body="{ data }">{{ formatQty(data.newStock) }}</template></Column>
                            <Column header="Diferencia">
                                <template #body="{ data }">
                                    <span :class="diffClass(data.difference)">{{ formatSignedQty(data.difference) }}</span>
                                    <small v-if="adjustmentDirection(data.difference)" :class="['ml-1', diffClass(data.difference)]">{{ adjustmentDirection(data.difference) }}</small>
                                </template>
                            </Column>
                            <Column header="Valor">
                                <template #body="{ data }">
                                    <span :class="diffClass(data.value)">{{ formatMoney(data.value) }}</span>
                                    <i v-if="data.valueUncertain" class="pi pi-question-circle ml-1 text-color-secondary" v-tooltip.top="'Sin compras con que valorarlo'"></i>
                                </template>
                            </Column>
                            <Column header="Motivo">
                                <template #body="{ data }">
                                    <div>{{ data.reasonLabel }}</div>
                                    <small class="text-color-secondary">{{ data.reason }}</small>
                                </template>
                            </Column>
                            <Column header="">
                                <template #body="{ data }">
                                    <Tag v-if="data.needsReview" value="Revisar" severity="warning"
                                         v-tooltip.top="'El stock previo era negativo: ¿falta registrar una compra?'" />
                                </template>
                            </Column>
                        </DataTable>
                    </TabPanel>
                    <TabPanel header="Por producto">
                        <DataTable :value="summary.byProduct" responsiveLayout="scroll" class="p-datatable-sm" :loading="loadingSummary">
                            <template #empty>Sin datos.</template>
                            <Column field="label" header="Producto" />
                            <Column field="adjustments" header="Ajustes" />
                            <Column header="Unidades perdidas"><template #body="{ data }">{{ formatQty(data.lossUnits) }}</template></Column>
                            <Column header="Unidades sobrantes"><template #body="{ data }">{{ formatQty(data.gainUnits) }}</template></Column>
                            <Column header="Pérdidas"><template #body="{ data }"><span class="text-red-500">{{ formatMoney(data.lossValue) }}</span></template></Column>
                            <Column header="Ganancias"><template #body="{ data }"><span class="text-blue-500">{{ formatMoney(data.gainValue) }}</span></template></Column>
                            <Column header="Neto"><template #body="{ data }"><span :class="diffClass(data.netValue)">{{ formatMoney(data.netValue) }}</span></template></Column>
                            <Column v-if="hasConsumption" header="Consumido"><template #body="{ data }">{{ formatQty(data.consumptionUnits) }}</template></Column>
                            <Column v-if="hasConsumption" header="Consumo"><template #body="{ data }">{{ formatMoney(data.consumptionValue) }}</template></Column>
                        </DataTable>
                    </TabPanel>
                    <TabPanel header="Por motivo">
                        <DataTable :value="summary.byReason" responsiveLayout="scroll" class="p-datatable-sm" :loading="loadingSummary">
                            <template #empty>Sin datos.</template>
                            <Column field="label" header="Motivo" />
                            <Column field="adjustments" header="Ajustes" />
                            <Column header="Pérdidas"><template #body="{ data }"><span class="text-red-500">{{ formatMoney(data.lossValue) }}</span></template></Column>
                            <Column header="Ganancias"><template #body="{ data }"><span class="text-blue-500">{{ formatMoney(data.gainValue) }}</span></template></Column>
                            <Column v-if="hasConsumption" header="Consumo"><template #body="{ data }">{{ formatMoney(data.consumptionValue) }}</template></Column>
                        </DataTable>
                    </TabPanel>
                    <TabPanel header="Por usuario">
                        <DataTable :value="summary.byCreator" responsiveLayout="scroll" class="p-datatable-sm" :loading="loadingSummary">
                            <template #empty>Sin datos.</template>
                            <Column field="label" header="Usuario" />
                            <Column field="adjustments" header="Ajustes" />
                            <Column field="needsReview" header="Para revisar" />
                            <Column header="Pérdidas"><template #body="{ data }"><span class="text-red-500">{{ formatMoney(data.lossValue) }}</span></template></Column>
                            <Column header="Ganancias"><template #body="{ data }"><span class="text-blue-500">{{ formatMoney(data.gainValue) }}</span></template></Column>
                            <Column header="Neto"><template #body="{ data }"><span :class="diffClass(data.netValue)">{{ formatMoney(data.netValue) }}</span></template></Column>
                        </DataTable>
                    </TabPanel>
                </TabView>
            </div>
        </div>
    </div>
</template>

<script>
import httpRequest from '../../../service/httpRequest';
import { localISODate } from '../../../service/dates';
import { REASONS, adjustmentDirection, diffClass, formatDate, formatMoney, formatQty, formatSignedQty, sourceLabel } from './inventoryFormat';

export default {
    name: 'StockAdjustmentLog',
    data() {
        return {
            loading: false,
            loadingSummary: false,
            items: [],
            currentPage: 1,
            pageSize: 25,
            totalItems: 0,
            totals: { lossValue: 0, gainValue: 0, netValue: 0, consumptionValue: 0 },
            summary: { byProduct: [], byCreator: [], byReason: [] },
            productSuggestions: [],
            filters: { product: null, source: null, reasonCode: null, initialDate: null, endDate: null, needsReview: false },
            reasonOptions: REASONS,
            sourceOptions: [
                { value: 'SYNC', label: 'Sincronización' },
                { value: 'COUNT', label: 'Conteo' },
                { value: 'MANUAL', label: 'Ajuste manual' },
            ],
        };
    },
    created() {
        if (!this.$can('inventoryManage')) {
            this.$router.replace('/dashboard');
            return;
        }
        this.loadAll(1);
    },
    computed: {
        // Sin insumos periódicos no hay consumo: la pantalla queda como siempre.
        hasConsumption() {
            return Number(this.totals.consumptionValue || 0) !== 0
                || (this.summary.byReason || []).some(r => r.key === 'CONSUMO');
        },
    },
    methods: {
        adjustmentDirection, diffClass, formatDate, formatMoney, formatQty, formatSignedQty, sourceLabel,
        filterPayload() {
            return {
                productId: this.filters.product && this.filters.product.productId ? this.filters.product.productId : null,
                source: this.filters.source,
                reasonCode: this.filters.reasonCode,
                needsReview: this.filters.needsReview,
                initialDate: this.toIso(this.filters.initialDate),
                endDate: this.toIso(this.filters.endDate),
            };
        },
        loadAll(page) {
            this.loadList(page);
            this.loadSummary();
        },
        async loadList(page) {
            this.loading = true;
            this.currentPage = page;
            try {
                const r = await httpRequest.getStockAdjustments({ ...this.filterPayload(), page, pageSize: this.pageSize });
                if (r.status === 200 && r.data) {
                    this.items = r.data.items || [];
                    this.totalItems = r.data.totalItems || 0;
                    this.totals = r.data.totals || this.totals;
                } else {
                    this.$toast.add({ severity: 'error', summary: 'Error', detail: r.error || 'No se pudo cargar', life: 4000 });
                }
            } catch (e) {
                console.error(e);
                this.$toast.add({ severity: 'error', summary: 'Error', detail: 'Fallo de red', life: 4000 });
            } finally {
                this.loading = false;
            }
        },
        async loadSummary() {
            this.loadingSummary = true;
            try {
                const r = await httpRequest.getStockAdjustmentSummary(this.filterPayload());
                if (r.status === 200 && r.data) this.summary = r.data;
            } catch (e) {
                console.error(e);
            } finally {
                this.loadingSummary = false;
            }
        },
        async searchProducts(query) {
            const r = await httpRequest.getSyncableProducts(query || null);
            this.productSuggestions = r.status === 200 && Array.isArray(r.data) ? r.data : [];
        },
        openCount(id) {
            this.$router.push({ path: '/dashboard/inventario/conteo/detalle', query: { id } });
        },
        toIso(d) {
            if (!d) return null;
            // Fecha de calendario local, no UTC: de noche toISOString() ya es «mañana» (H64).
            return localISODate(d);
        },
    },
};
</script>

<style scoped>
.apply-filter-btn {
    background: var(--primary-color) !important;
    border-color: var(--primary-color) !important;
    color: var(--primary-color-text, #fff) !important;
}
</style>
